import {useRef, useCallback, useState} from 'react'
import {Hero, LogoSuite, Image, Text} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {IntroHero} from './IntroHero'
import {LOGOS} from './IntroLogos.data'
import {Spacer} from './_shared'
import HeroCarousel from './components/HeroCarousel/HeroCarousel'
import HeroCarouselControls, {
  type HeroCarouselControlsProps,
} from './components/HeroCarousel/HeroCarouselControls/HeroCarouselControls'

const COPY = {
  hero: {
    heading: 'Build and ship software on a single, collaborative platform',
    description:
      'Join the world’s most widely adopted AI-powered developer platform where millions of developers, businesses, and the largest open source community build software that advances humanity.',
    cta1: {
      label: 'Sign up',
      url: '/signup?source=form-home-signup',
    },
    cta2: {
      label: 'Try GitHub Copilot',
      subLabel: '30 days free',
      url: '/features/copilot',
    },
  },
  carousel: {
    items: [
      {
        label: 'Code',
        value: 'code',
        description: 'Build code quickly and more securely with GitHub Copilot embedded throughout your workflows.',
      },
      {
        label: 'Plan',
        value: 'plan',
        description: 'Track and coordinate your work with GitHub Issues, GitHub Projects, and insights.',
      },
      {
        label: 'Collaborate',
        value: 'collaborate',
        description:
          'Work easily with your team in real time with GitHub Pull Requests, code reviews, and GitHub Discussions.',
      },
      {
        label: 'Automate',
        value: 'automate',
        description:
          'Streamline your workflows with automated CI/CD, testing, planning, project management, issue labeling, approvals, onboarding, and more.',
      },
      {
        label: 'Secure',
        value: 'secure',
        description: 'Leverage tooling to detect, review, and fix vulnerabilities before they reach production.',
      },
    ],
    aria: {
      // TODO: Update once known
      playButton: {
        play: 'Play',
        pause: 'Pause',
      },
      // TODO: Update once known
      controlsToggle: '',
    },
  },
  logoSuite: {
    heading: 'GitHub is used by',
  },
}

export default function Intro() {
  const [currentCarouselScreenIndex, setCurrentCarouselScreenIndex] = useState<number>(0)
  const heroRef = useRef<HTMLDivElement | null>(null)

  const setCopyScrollOpacity = useCallback((copyScrollOpacity: number) => {
    if (heroRef.current) {
      heroRef.current.style.opacity = `${copyScrollOpacity}` // Set the desired opacity value
    }
  }, [])

  const setCopyScrollScale = useCallback((copyScrollScale: number) => {
    if (heroRef.current) {
      heroRef.current.style.transform = `scale(${copyScrollScale})` // Set the desired scale value
    }
  }, [])

  const startCopyAnimation = useCallback(() => {
    if (heroRef.current) {
      heroRef.current.classList.add('is-visible')
    }
  }, [])

  return (
    <section id="hero">
      <div className="lp-Intro">
        {/* Header 72 + Global Banner 42 = 114px */}
        <Spacer size="114px" />
        <Spacer size="0" size768="60px" size1012="96px" />

        <IntroHero
          setCopyScrollOpacity={setCopyScrollOpacity}
          setCopyScrollScale={setCopyScrollScale}
          startCopyAnimation={startCopyAnimation}
        />

        {/* Hero */}
        <Hero ref={heroRef} className="lp-IntroHero sh-CtaWithTrial" data-hpc align="center">
          <Hero.Heading size="2">{COPY.hero.heading}</Hero.Heading>

          <Hero.Description size="300">{COPY.hero.description}</Hero.Description>

          <Hero.PrimaryAction
            href={COPY.hero.cta1.url}
            hasArrow={false}
            {...getAnalyticsEvent({action: 'sign_up', tag: 'button', context: 'CTAs', location: 'hero'})}
          >
            {COPY.hero.cta1.label}
          </Hero.PrimaryAction>
          <Hero.SecondaryAction
            className="lp-IntroHero-secondaryAction"
            href={COPY.hero.cta2.url}
            hasArrow={false}
            {...getAnalyticsEvent({action: 'try_copilot', tag: 'button', context: 'CTAs', location: 'hero'})}
          >
            {COPY.hero.cta2.label}
            <Text className="sh-CtaWithTrial-label" size="100">
              {COPY.hero.cta2.subLabel}
            </Text>
          </Hero.SecondaryAction>
        </Hero>

        <Spacer size="64px" size768="560px" size1012="660px" />

        {/* Carousel */}
        <HeroCarousel
          currentScreenIndex={currentCarouselScreenIndex}
          onAutoPlayTimeout={setCurrentCarouselScreenIndex}
          playButtonAriaLabel={COPY.carousel.aria.playButton}
        />
      </div>

      {/* Carousel controls */}
      <HeroCarouselControls
        items={COPY.carousel.items as HeroCarouselControlsProps['items']}
        currentIndex={currentCarouselScreenIndex}
        onChange={setCurrentCarouselScreenIndex}
        toggleAriaLabel={COPY.carousel.aria.controlsToggle}
      />

      <Spacer size="48px" size1012="80px" />

      {/* Logos */}
      <LogoSuite className="IntroLogoSuite">
        <LogoSuite.Heading visuallyHidden>{COPY.logoSuite.heading}</LogoSuite.Heading>
        <LogoSuite.Logobar marquee marqueeSpeed="slow">
          {LOGOS.map((logo, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Image key={index} src={logo.image} alt={logo.alt} style={{height: logo.height}} loading="lazy" />
          ))}
        </LogoSuite.Logobar>
      </LogoSuite>
    </section>
  )
}

try{ Intro.displayName ||= 'Intro' } catch {}